import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useDispatch, useSelector } from 'react-redux'
import Table from 'modules/Statistics/subcomponents/Layout/Table'
import PropTypes from 'prop-types'
import { notificationShow } from 'modules/Notifications/actions'
import queryString from 'query-string'
import prepareFilterParams from 'utils/prepareFilterParams'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Button, Grid, Icon } from 'gu-ui-kit'
import downloadFile from 'utils/downloadFile'
import { useTranslation } from 'react-i18next'
import MultipleSelect from 'components/MultipleSelect'
import { URL_FILE_XLS } from 'api/statistics/v2'
import { useTableConfig } from 'modules/Statistics/Main/subcomponents/TableComponent/hooks/useTableConfig'
import Select from 'components/Select'
import useEnableHiding from 'modules/Statistics/hooks/useEnableHiding'
import clsx from 'clsx'
import GroupManager from 'modules/Statistics/Main/subcomponents/GroupManager'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyStatistics } from 'images/illustration/empty-statistics.svg'
import { ReactComponent as EmptyStatisticsGroups } from 'images/illustration/empty-statistics-groups.svg'
import {
  CREATE_GROUP,
  DROP_TABLE_DATA,
  FETCH_ACCOUNTS,
  FETCH_GROUPS,
  FETCH_TABLE,
} from 'modules/Statistics/types'

const TableComponent = ({
  filters,
  isDemoMode = false,
  chartVisible,
  setChartVisible,
  setShowedGroupOnChartId,
  showedGroupOnChartId,
  handleAccountAttach,
  handleChangeGroup,
  groupBy,
  setGroupBy,
}) => {
  const { table, groups = [], accounts = [] } = useSelector(({ statistics }) => statistics)
  const { lg, md, fb, xl, xxl } = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [sorting, setSorting] = useState()

  const [statisticxsainTableConfig, setStatisticxsainTableConfig] = useLocalStorage(
    `statisticxsainTableConfig.${groupBy}`,
    { columnVisibility: {} },
  )

  const [columnVisibility, setColumnVisibility] = useState(
    statisticxsainTableConfig.columnVisibility,
  )

  const enrichedGroups = useMemo(() => {
    if (!groups.length || !table.data.length) return groups

    const accountsByGroupId = table.data.reduce((map, row) => {
      const groupId = String(row?.group?.id ?? row?.id)
      if (groupId && row?.accounts) {
        map[groupId] = row.accounts
      }
      return map
    }, {})

    return groups.map((group) => {
      const accounts = accountsByGroupId[String(group?.id)]
      return accounts ? { ...group, accounts } : group
    })
  }, [groups, table.data])

  const tableConfig = useTableConfig({
    groups: enrichedGroups,
    groupBy,
    filters,
    summary: table.summary,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    setShowedGroupOnChartId,
    showedGroupOnChartId,
    className: clsx('margin-bottom-8', styles['table'], styles['sticky'], {
      [styles[`table__${groupBy}`]]: groupBy,
    }),
    handleAccountAttach,
    handleChangeGroup,
    isDemoMode,
  })

  useEffect(() => {
    if (groupBy === 'groups') {
      if (!showedGroupOnChartId && table.data.length > 0) {
        setShowedGroupOnChartId(table.data?.[0]?.group.id)
      }
    } else {
      setShowedGroupOnChartId(null)
    }
  }, [table.data])

  const enableHiding = useEnableHiding(tableConfig)

  const setDefaultVisibility = useCallback(() => {
    setColumnVisibility(() =>
      enableHiding.reduce((acc, item) => {
        acc[item.value] = true
        return acc
      }, {}),
    )
  }, [setColumnVisibility, enableHiding])

  useEffect(() => {
    setStatisticxsainTableConfig({ columnVisibility })
  }, [columnVisibility])

  useEffect(() => {
    setDefaultVisibility()
  }, [groupBy])

  useEffect(() => {
    setSorting(() => tableConfig.columns.length && [{ id: tableConfig.columns[0].id, desc: true }])
    setDefaultVisibility()
  }, [])

  const fileUrl = useCallback(
    (url) => {
      const { id = null, desc = false } = sorting?.[0] ?? {}
      const sortingProp = id ? { sort_by: id, direction: desc ? 'desc' : 'asc' } : {}

      const search = queryString.stringify(
        prepareFilterParams({
          ...filters,
          columns: Object.keys(columnVisibility)
            .filter(({ id }) => id !== 'account.id')
            .map(({ id }) => (id === 'cost' && filters?.include_vat ? 'cost_with_vat' : id)),
          offset: 0,
          limit: table.pagination?.total || 100,
          ...sortingProp,
        }),
        {
          arrayFormat: 'bracket',
        },
      )

      return url + '?' + search
    },
    [filters, columnVisibility, sorting, table.pagination],
  )

  const selectedColumns = useMemo(
    () =>
      Object.keys(columnVisibility).reduce((acc, item) => {
        if (columnVisibility[item]) {
          acc.push(item)
        }
        return acc
      }, []),
    [columnVisibility],
  )

  const handleDownloadError = useCallback(
    (error) =>
      dispatch(
        notificationShow({
          type: 'warning',
          message: error,
          delay: 20000,
        }),
      ),
    [dispatch],
  )

  useEffect(() => {
    if (groupBy === 'groups' || groupBy === 'accounts') {
      dispatch({
        type: FETCH_GROUPS,
      })
      dispatch({
        type: FETCH_ACCOUNTS,
      })
    }
  }, [groupBy])

  const dropTableData = useCallback(
    () => dispatch({ type: DROP_TABLE_DATA }),
    [],
  )

  const onTableParamsUpdate = useCallback(
    (params) => dispatch({ type: FETCH_TABLE, payload: params, isDemoMode }),
    [isDemoMode],
  )

  const defaultColumnSize = useMemo(() => ({ size: 192 }), [])
  const mergedTableConfig = useMemo(
    () => ({
      ...tableConfig,
      ...(groupBy !== 'groups' ? { defaultColumn: defaultColumnSize } : {}),
    }),
    [tableConfig, groupBy, defaultColumnSize],
  )

  const TableEmptyResults = useMemo(
    () =>
      groupBy === 'groups' && groups?.length === 0 && table.loading === false ? (
        <EmptyResults
          header={t('statistics::table.groups.empty.heading')}
          text={t('statistics::table.groups.empty.text')}
          image={<EmptyStatisticsGroups />}
          actionButton={
            accounts.length > 0 ? (
              <GroupManager
                isDemoMode={isDemoMode}
                onApply={({ newGroupData }) => {
                  dispatch({ type: CREATE_GROUP, payload: { newGroupData } })
                }}
                selectedAccountIds={[]}
                ButtonComponent={() => (
                  <Button
                    noLabel={fb}
                    variant={'primary'}
                    noWrap
                    size={'m'}
                    startIcon={<Icon name={'plus'} />}
                  >
                    {t('statistics::table.create_group')}
                  </Button>
                )}
              />
            ) : null
          }
        />
      ) : (
        <EmptyResults image={<EmptyStatistics />} />
      ),
    [groupBy, groups, table.loading, accounts],
  )

  return (
    <>
      <Grid
        gap={16}
        alignItems={'center'}
        className={styles['table_switcher-panel']}
        wrap={!md}
        justifyContent={'between'}
      >
        <Grid
          wrap={false}
          direction={'row'}
          gap={10}
          alignItems={'center'}
        >
          {groupBy === 'groups' && accounts.length > 0 && (
            <GroupManager
              isDemoMode={isDemoMode}
              onApply={({ newGroupData }) => {
                dispatch({ type: CREATE_GROUP, payload: { newGroupData } })
              }}
              selectedAccountIds={[]}
              ButtonComponent={() => (
                <Button
                  noLabel={!lg}
                  variant={'primary'}
                  noWrap
                  size={'m'}
                  startIcon={<Icon name={'plus'} />}
                >
                  {t('statistics::table.create_group')}
                </Button>
              )}
            />
          )}
          <Select
            buttonProps={{
              variant: 'secondary',
              size: !md ? 's' : 'm',
              grey: true,
              fullWidth: !md,
              align: !md ? 'center' : 'left',
            }}
            {...{
              key: 'table-switcher-table-group-switcher',
              name: 'table-group-switcher',
              value: {
                value: groupBy,
                label: `${t('statistics::table_switcher.group_by')}: ${t(
                  'statistics::table_switcher.by',
                  { context: groupBy },
                )}`,
              },
              values: [
                {
                  value: 'accounts',
                  label: t('statistics::table_switcher.by', {
                    context: 'accounts',
                  }),
                },
                {
                  value: 'networks',
                  label: t('statistics::table_switcher.by', {
                    context: 'networks',
                  }),
                },
                {
                  value: 'groups',
                  label: t('statistics::table_switcher.by', {
                    context: 'groups',
                  }),
                },
              ],
              onChange: setGroupBy,
            }}
          />
        </Grid>

        <Grid
          fluid={!md}
          wrap={false}
          gap={!md ? 8 : 16}
          alignItems={'center'}
          justifyContent={!md ? 'space-between' : 'flex-end'}
        >
          <MultipleSelect
            buttonProps={{
              size: !md ? 's' : 'm',
            }}
            name={'columns'}
            type={'multiple'}
            placeholder={t('statistics::table_switcher.choose_columns_placeholder')}
            listTitle={t('statistics::table_switcher.choose_columns')}
            value={selectedColumns}
            values={enableHiding}
            onChange={(newSelection) =>
              setColumnVisibility(
                enableHiding.reduce((acc, item) => {
                  acc[item.value] = newSelection.includes(item.value)
                  return acc
                }, {}),
              )
            }
            onReset={setDefaultVisibility}
            apply={t('common::buttons.apply')}
            dropSelection={t('common::buttons.reset')}
          />

          {[
            {
              type: 'xls',
              fileUrl: fileUrl(URL_FILE_XLS({ groupBy })),
            },
          ].map(({ type, fileUrl }) => (
            <Button
              onClick={() => downloadFile(fileUrl, 'statistics', type).catch(handleDownloadError)}
              key={type}
              variant={'ghost'}
              grey
              noLabel={md && !lg}
              size={!md ? 's' : 'm'}
              startIcon={<Icon name={'file-' + type} />}
            >
              {t('statistics::buttons.download.label')}
            </Button>
          ))}

          {chartVisible !== undefined && (
            <Button
              noWrap
              onClick={() => setChartVisible(!chartVisible)}
              className={styles['hide-chart-button']}
              variant={'ghost'}
              grey
              size={!md ? 's' : 'm'}
              noLabel={groupBy === 'groups' ? !xxl : !xl}
              startIcon={
                <Icon
                  name={chartVisible ? 'eye-hidden' : 'eye-visible'}
                  color={'inherit'}
                />
              }
            >
              {t('statistics::filters.chart_visiblity', {
                context: chartVisible ? 'hide' : 'show',
              })}
            </Button>
          )}
        </Grid>
      </Grid>
      <Table
        EmptyResults={() => TableEmptyResults}
        groups={groups}
        table={table}
        isDemoMode={isDemoMode}
        tableConfig={mergedTableConfig}
        groupBy={groupBy}
        filters={filters}
        dropTableData={dropTableData}
        onTableParamsUpdate={onTableParamsUpdate}
      />
    </>
  )
}

TableComponent.propTypes = {
  handleAccountAttach: PropTypes.func,
  filters: PropTypes.object.isRequired,
  isDemoMode: PropTypes.bool,
  chartVisible: PropTypes.bool,
  setChartVisible: PropTypes.func,
  setShowedGroupOnChartId: PropTypes.func,
  showedGroupOnChartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleChangeGroup: PropTypes.func,
  groupBy: PropTypes.string.isRequired,
  setGroupBy: PropTypes.func.isRequired,
}

export default TableComponent
